import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetNotificationQueryKey,
  getGetNotificationUnreadCountQueryKey,
  useGetNotification,
  useGetNotificationUnreadCount,
  usePutNotificationIdRead,
  usePutNotificationReadAll,
} from '../api/notification-api/notification-api';
import { NotificationDto, NotificationEntityType } from '../api/model';

const POLL_INTERVAL_MS = 30_000;

export const INBOX_LIMIT = 50;

const INBOX_KEY = getGetNotificationQueryKey();
const UNREAD_KEY = getGetNotificationUnreadCountQueryKey();

interface UseNotificationInboxOptions {
  enabled?: boolean;
  isOpen?: boolean;
}

// Where clicking a notification should land.
export interface NotificationTarget {
  projectId: number;
  section: string;
  taskId?: number;
}

export const notificationTarget = (notification: NotificationDto): NotificationTarget | null => {
  const { projectId, entityType, entityId } = notification;
  if (!projectId) return null;

  switch (entityType) {
    case NotificationEntityType.TASK:
      // The task id opens the detail modal directly, not just the section.
      return { projectId, section: 'tasks', taskId: entityId };
    case NotificationEntityType.ACTION_POINT:
      return { projectId, section: 'task-action-points' };
    case NotificationEntityType.CHECKPOINT:
      return { projectId, section: 'check-plan' };
    case NotificationEntityType.DELIVERABLE:
      return { projectId, section: 'deliverables' };
    case NotificationEntityType.PHASE:
      return { projectId, section: 'phase-plan-v2' };
    default:
      return { projectId, section: 'phase-plan-v2' };
  }
};

export const useNotificationInbox = ({ enabled = true, isOpen = false }: UseNotificationInboxOptions = {}) => {
  const queryClient = useQueryClient();

  // The global query client
  const { data: unreadCount = 0 } = useGetNotificationUnreadCount({
    query: {
      enabled,
      staleTime: 0,
      refetchInterval: POLL_INTERVAL_MS,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    },
  });

  const { data, isLoading } = useGetNotification({
    query: {
      enabled: enabled && isOpen,
      staleTime: 0,
      refetchOnMount: true,
    },
  });

  const notifications = useMemo(() => data ?? [], [data]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: INBOX_KEY });
    queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
  }, [queryClient]);

  const markReadMutation = usePutNotificationIdRead({
    mutation: {
      onMutate: ({ id }) => {
        queryClient.setQueryData<NotificationDto[]>(INBOX_KEY, prev =>
          prev?.map(item => (item.id === id ? { ...item, read: true } : item)),
        );
        queryClient.setQueryData<number>(UNREAD_KEY, prev =>
          typeof prev === 'number' ? Math.max(0, prev - 1) : prev,
        );
      },
      onSettled: invalidate,
    },
  });

  const markAllReadMutation = usePutNotificationReadAll({
    mutation: {
      onMutate: () => {
        queryClient.setQueryData<NotificationDto[]>(INBOX_KEY, prev =>
          prev?.map(item => ({ ...item, read: true })),
        );
        queryClient.setQueryData<number>(UNREAD_KEY, 0);
      },
      onSettled: invalidate,
    },
  });

  const markAsRead = useCallback(
    (id: number | undefined) => {
      if (!id) return;
      const target = notifications.find(item => item.id === id);
      if (!target || target.read) return;
      markReadMutation.mutate({ id });
    },
    [notifications, markReadMutation],
  );

  const markAllAsRead = useCallback(() => {
    if (unreadCount === 0) return;
    markAllReadMutation.mutate();
  }, [unreadCount, markAllReadMutation]);

  return { notifications, unreadCount, isLoading, markAsRead, markAllAsRead };
};
